import { Component, inject, signal } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { ChevronDown, ChevronUp, LucideAngularModule } from "lucide-angular";
import { InfoboxDto } from "../../../api";
import { filter } from "rxjs";
import { InfoboxQueryService } from "../../queries/infobox-queries.service";
import { DateUtil } from "../../util/DateUtil";

@Component({
  selector: "app-infobox-display-uif",
  imports: [LucideAngularModule],
  template: `
    @if ((infoboxes.data()?.length ?? 0) > 0) {
    <div class="flex flex-col gap-4">
      @for (infobox of infoboxes.data(); track infobox.id) {
      @let lines = getLines(infobox.anzeigetext);
      @let expanded = getExpanded(infobox.id);
      @let showToggle = lines.length > 1;
      <div class="infobox-card">
        <div class="infobox-header">
          <span class="infobox-header-icon">i</span>
          <span class="infobox-header-title">INFOBOX</span>
          @if (showToggle) {
          <button
            (click)="toggleExpanded(infobox.id)"
            class="infobox-toggle"
            type="button"
            [attr.title]="expanded() ? 'Einklappen' : 'Ausklappen'"
          >
            <i-lucide
              [img]="expanded() ? iconChevronDown : iconChevronUp"
              size="14"
            ></i-lucide>
          </button>
          }
        </div>
        <div class="infobox-body" [class.infobox-body-collapsed]="showToggle && !expanded()">
          <ul class="infobox-list">
            @for (line of (showToggle && !expanded()) ? [lines[0]] : lines; track $index) {
            <li class="infobox-list-item">{{ line }}</li>
            }
          </ul>
        </div>
      </div>
      }
    </div>
    }
  `,
  styles: `
    .infobox-card {
      border: 2px solid var(--color-primary);
      transition: all 0.3s ease;
    }

    .infobox-header {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      background: var(--color-primary);
      color: white;
      position: relative;
    }

    .infobox-header-icon {
      width: 16px;
      height: 16px;
      border: 2px solid white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-weight: 500;
      font-size: 13px;
      font-family: 'Source Sans 3', sans-serif;
      line-height: 1;
      margin-top: -2px;
    }

    .infobox-header-title {
      font-weight: 700;
      font-size: 12px;
      font-family: 'Source Sans 3', sans-serif;
    }

    .infobox-toggle {
      margin-left: auto;
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 2px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.85;
      transition: opacity 0.15s ease;
    }

    .infobox-toggle:hover {
      opacity: 1;
    }

    .infobox-body {
      padding: 12px 16px;
      background: white;
      max-height: 145px;
      overflow-y: auto;
    }

    .infobox-body-collapsed {
      max-height: none;
      overflow-y: visible;
    }

    .infobox-body-collapsed .infobox-list-item {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .infobox-list {
      margin: 0;
      padding-left: 20px;
      list-style: none;
    }

    .infobox-list-item {
      font-size: 14px;
      line-height: 1.5;
      color: #333;
      font-family: 'Source Sans 3', sans-serif;
      margin-bottom: 4px;
      display: list-item;
      list-style-type: disc;
    }

    .infobox-list-item:last-child {
      margin-bottom: 0;
    }
  `,
})
export class InfoboxDisplayUifComponent {
  private router = inject(Router);
  private infoboxService = inject(InfoboxQueryService);

  iconChevronUp = ChevronUp;
  iconChevronDown = ChevronDown;

  currentRoute = signal("");
  infoboxes = this.infoboxService.getInfoboxesForPathQuery(signal(true), this.currentRoute);

  private expandedStates = new Map<string, ReturnType<typeof signal>>();

  constructor() {
    this.currentRoute.set(this.router.url);

    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.currentRoute.set(this.router.url);
      this.expandedStates.clear();
    });
  }

  getExpanded(id: string | undefined): ReturnType<typeof signal> {
    if (!id) return signal(true);
    if (!this.expandedStates.has(id)) {
      this.expandedStates.set(id, signal(true));
    }
    return this.expandedStates.get(id)!;
  }

  toggleExpanded(id: string | undefined) {
    if (!id) return;
    const s = this.getExpanded(id);
    s.update((v) => !v);
  }

  getLines(text: string | undefined | null): string[] {
    if (!text) return [];
    const currentYear = DateUtil.today().getFullYear();
    const yearMinus1 = currentYear - 1;
    return text
      .split("\n")
      .map((line) => line.replace(/20XX-1/g, String(yearMinus1)).replace(/20XX/g, String(currentYear)))
      .filter((line) => line.trim() !== "");
  }
}
